import { computed, signal } from "@preact/signals";

import { Cookie, persist } from "~/util/persist.js";
import { clearCache } from "~/util/query.js";
import { object, optional, parse, string } from "~/util/validate.js";

/**
 * @typedef {object} Session
 * @property {string} token
 * @property {string} [machineId] Set for sessions bound to this machine, which can host.
 */

/**
 * @typedef {object} SessionOwner
 * @property {(callback: (session: Session | undefined) => void) => () => void} subscribe
 * @property {(session: Session | undefined) => void} set
 */

const session = signal(/** @type {Session | undefined} */ (undefined));

export const token = computed(() => session.value?.token);
export const loggedIn = computed(() => session.value !== undefined);

// responses cached under one session must not be served under another
token.subscribe(() => clearCache());

/** @type {SessionOwner | undefined} */
let owner;

function boundOwner() {
	if (!owner) throw new Error("The session has no owner; call bindSession first.");
	return owner;
}

/** @param {SessionOwner} value */
export function bindSession(value) {
	if (owner) throw new Error("The session already has an owner.");
	owner = value;

	value.subscribe(next => {
		session.value = next;
	});
}

/** @param {Session} value */
export function setSession(value) {
	boundOwner().set(value);
}

/** Called on log out and whenever the API rejects the token. */
export function clearSession() {
	boundOwner().set(undefined);
}

/** @returns {SessionOwner} */
export function cookieSession() {
	const stored = signal(/** @type {Session | undefined} */ (undefined));
	persist(stored, "session", {
		parse: value => parse(object({ token: string, machineId: optional(string) }), value),
		storage: Cookie,
	});

	return {
		subscribe: callback => stored.subscribe(callback),
		set: value => {
			stored.value = value;
		},
	};
}
